<template>
  <section class="calculator-module">
    <div class="module-header">
      <h2>{{ t('quarantine.title').value }}</h2>
      <span class="toggle-icon">▼</span>
    </div>
    <div class="module-content">
      <!-- 方案选择阶段 -->
      <QuarantineSelector
        v-if="!selectedPlan"
        v-model="selectedPlan"
        :initial-water-volume="waterVolume"
        @start="startQuarantine"
      />

      <!-- 进度追踪阶段 -->
      <div v-else class="tracker-container">
        <QuarantineTimeline
          :selected-plan="selectedPlan"
          :water-volume="waterVolume"
          :start-date="startDate"
          :current-day="currentDay"
          :completed-steps="completedSteps"
          @show-step-detail="showStepDetail"
          @reset-plan="resetPlan"
          @show-selector="showSelector"
        />

        <!-- 步骤详情模态框 -->
        <StepDetailModal
          :show="showStepModal"
          :step="currentStep"
          :water-volume="waterVolume"
          @update:show="showStepModal = $event"
          @complete="completeStep"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import QuarantineSelector from './QuarantineSelector.vue'
import QuarantineTimeline from './QuarantineTimeline.vue'
import StepDetailModal from './StepDetailModal.vue'
import { useI18n } from '../composables/useI18n'
import {
  type QuarantinePlan,
  type QuarantineStep,
  type FishStatus,
  useQuarantinePlan
} from '../composables/useQuarantinePlan'

const { t } = useI18n()

// 使用检疫计划组合式函数
const quarantinePlan = useQuarantinePlan()
const selectedPlan = quarantinePlan.selectedPlan
const waterVolume = quarantinePlan.waterVolume
const startDate = quarantinePlan.startDate
const currentDay = quarantinePlan.currentDay
const completedSteps = quarantinePlan.completedSteps
const completeStepLogic = quarantinePlan.completeStep
const resetPlanLogic = quarantinePlan.resetPlan

// 本地状态
const showStepModal = ref(false)
const currentStep = ref<QuarantineStep | null>(null)

// 开始检疫计划
const startQuarantine = (plan: QuarantinePlan, volume: number) => {
  selectedPlan.value = plan
  waterVolume.value = volume
  startDate.value = new Date().toISOString().split('T')[0]
  
  // 保存到本地存储
  saveToLocalStorage()
}

// 显示步骤详情
const showStepDetail = (step: QuarantineStep | null) => {
  currentStep.value = step
  showStepModal.value = true
}

// 完成步骤
const completeStep = (step: QuarantineStep | null, fishStatus: FishStatus) => {
  if (step && selectedPlan.value) {
    const stepIndex = selectedPlan.value.steps.findIndex((s: QuarantineStep) => s.day === step.day && s.name === step.name)
    if (stepIndex !== -1) {
      console.log(`找到步骤索引: ${stepIndex}, 步骤: ${step.name}`)
      completeStepLogic(stepIndex, fishStatus)
      saveToLocalStorage()
    } else {
      console.error(`未找到步骤: ${step.name}, 天数: ${step.day}`)
    }
  }
}

// 重置计划
const resetPlan = () => {
  resetPlanLogic()
  clearLocalStorage()
}

// 显示选择器
const showSelector = () => {
  selectedPlan.value = null
}

// 本地存储管理
const saveToLocalStorage = () => {
  const data = {
    selectedPlan: selectedPlan.value,
    waterVolume: waterVolume.value,
    startDate: startDate.value,
    completedSteps: Array.from(completedSteps.value)
  }
  localStorage.setItem('quarantineTracker', JSON.stringify(data))
}

const loadFromLocalStorage = () => {
  const saved = localStorage.getItem('quarantineTracker')
  if (saved) {
    try {
      const data = JSON.parse(saved)
      selectedPlan.value = data.selectedPlan
      waterVolume.value = data.waterVolume || 0
      startDate.value = data.startDate || ''
      
      if (data.completedSteps) {
        completedSteps.value = new Set(data.completedSteps)
      }
    } catch (error) {
      console.error('加载检疫追踪数据失败:', error)
      clearLocalStorage()
    }
  }
}

const clearLocalStorage = () => {
  localStorage.removeItem('quarantineTracker')
}

// 组件挂载时加载保存的数据
onMounted(() => {
  loadFromLocalStorage()
  
  // 设置定时器，每分钟更新一次当前天数
  const updateInterval = setInterval(() => {
    // 强制触发响应式更新
    if (selectedPlan.value && startDate.value) {
      const start = new Date(startDate.value)
      const now = new Date()
      const diffTime = Math.abs(now.getTime() - start.getTime())
      const days = Math.floor(diffTime / (1000 * 60 * 60 * 24))
      // 通过更新startDate来触发currentDay的重新计算
      const tempDate = startDate.value
      startDate.value = ''
      startDate.value = tempDate
      console.log(`定时更新检疫天数: ${days}`)
    }
  }, 60000) // 每分钟更新一次
  
  // 组件卸载时清除定时器
  onUnmounted(() => {
    clearInterval(updateInterval)
  })
})
</script>

<style scoped>
.tracker-container {
  max-width: 100%;
  margin: 0 auto;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .tracker-container {
    padding: 0;
  }
}
</style>